import * as React from 'react';
import moment = require('moment');
import { AvatarSmall, PersonItem } from 'components';
import { PortalPageSection } from 'components/PortalStructure';
import { IAppointment } from 'models/shop/appointments';
import { formatCurrency, formatDuration } from 'helpers/common';
import { OrderPaymentStatus, OrderStatus } from 'models';
import { translate } from 'react-i18next';
import 'moment-timezone';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { Link } from 'react-router';
import { IBookedOrderItemData, IOrderItem } from 'models/orders/order';
import renderIcon from 'helpers/renderIcon';

interface IProps {
	type: 'incoming' | 'past';
	appointment: IAppointment;
	/**
	 * for mobile view
	 */
	isShopAppointment: boolean;
	isShopCustomerAppointment?: boolean;
	showStartEndTime?: boolean;
	currencyKey?: number;
	dontMarkCancelled?: boolean;

	/**
	 * Return appointment info without shop info and PortalPageSection wrapper?
	 */
	onlyAppointmentInfo?: boolean;
	/**
	 * Only applies if onlyAppointmentInfo is true,
	 * default:false
	 */
	noPadding?: boolean;
	customClassName?: string;
	showCancelButton?: boolean;
	t: Function;
}

export const Appointment = translate()(({
	/**type,**/ appointment,
	isShopAppointment,
	isShopCustomerAppointment,
	currencyKey,
	dontMarkCancelled,
	onlyAppointmentInfo = false,
	noPadding = true,
	t,
	customClassName,
	showStartEndTime /**showCancelButton = false */,
}: IProps) => {
	if (currencyKey === undefined && appointment?.shopOrder?.currencyKey) {
		currencyKey = appointment.shopOrder.currencyKey;
	}

	const firstOrderItem: IOrderItem = Array.isArray(appointment.orderItem)
		? appointment.orderItem[0]
		: appointment.orderItem;
	const timezone = Array.isArray(appointment.orderItem)
		? appointment.orderItem[0].officeZoneInfo
		: appointment.orderItem.officeZoneInfo;
	const allBookedOrderItemData: IBookedOrderItemData[] = Array.isArray(appointment.orderItem)
		? [].concat.apply(
				[],
				appointment.orderItem.map((oi) => [...oi.bookedOrderItemDatas]),
		  )
		: firstOrderItem.bookedOrderItemDatas; // Put all orderItems bookedOrderItemData inside a single array
	////////////////////////////////////////////////////
	// Determine service length
	////////////////////////////////////////////////////
	let firstStartTime = '';
	let lastEndTime = '';

	allBookedOrderItemData.forEach((x, index) => {
		if (index === 0) {
			firstStartTime = x.startTime;
			lastEndTime = x.endTime;
			return;
		}
		if (moment(x.startTime).isBefore(moment(firstStartTime))) {
			firstStartTime = x.startTime;
		}
		if (moment(x.endTime).isAfter(moment(lastEndTime))) {
			lastEndTime = x.endTime;
		}
	});

	////////////////////////////////////////////////////
	// Render services
	////////////////////////////////////////////////////
	const renderShopServices = (): JSX.Element[] => {
		// Sort by start and end time
		const sortedBookedOrderItemData = allBookedOrderItemData.sort((a, b) => {
			if (moment(a.startTime).isBefore(b.startTime)) {
				return -1;
			} else if (moment(a.startTime).isAfter(b.startTime)) {
				return 1;
			} else {
				return 0;
			}
		});

		return sortedBookedOrderItemData.map((x, index) => {
			const resource = {
				name: x.resourceName,
				picture: x.resourcePicture,
			};

			const totalDurationInSeconds = moment(x.endTime).diff(moment(x.startTime), 'seconds');

			return (
				<div className={`serviceContainer row justify-content-between align-items-center`} key={index}>
					<div className="col-12">
						<p className="serviceContainer__serviceName">{x.bookingName ? x.bookingName : ''}</p>
						<div className="serviceContainer__durationContainer">
							{showStartEndTime ? (
								<span className="serviceContainer__startEndTime">
									{momentWithTimezone(timezone, x.startTime).format('LT - ')}
									{momentWithTimezone(timezone, x.endTime).format('LT')}
								</span>
							) : null}
							<span className="serviceContainer__duration">{formatDuration(totalDurationInSeconds)}</span>
						</div>
					</div>

					<div className="col-12">
						<div className="serviceContainer__serviceInfo">
							<PersonItem user={resource} />
							<div className="serviceContainer__priceContainer">
								<span className="serviceContainer__price">{formatCurrency(currencyKey, x.bookingPrice || 0)}</span>
							</div>
						</div>
					</div>
				</div>
			);
		});
	};

	// For user appointments
	let totalPrice = 0;

	if (Array.isArray(appointment.orderItem)) {
		appointment.orderItem.map((oi) => {
			totalPrice += oi.price;
		});
	}

	const cancelled =
		appointment.shopOrder.status == OrderStatus.Rejected || appointment.shopOrder.status == OrderStatus.Canceled;
	const booked =
		appointment.shopOrder.status === OrderStatus.Pending || appointment.shopOrder.status === OrderStatus.Processing;
	// Display as completed for now if the time is in the past as we don't update statuses correctly
	const completed =
		appointment.shopOrder.status === OrderStatus.Shipped ||
		appointment.shopOrder.status === OrderStatus.PartiallyShipped ||
		(appointment.shopOrder.status === OrderStatus.Processing &&
			firstOrderItem?.bookedOrderItemDatas?.[0]?.endTime &&
			momentWithTimezone(timezone, firstOrderItem.bookedOrderItemDatas[0].endTime).isBefore(new Date()));

	let orderStatusElement: JSX.Element = null;

	if (
		appointment?.order?.paymentStatus === OrderPaymentStatus.Verified ||
		appointment?.order?.message?.includes('Original paid order id') === true
	) {
		orderStatusElement = <span className="paid-online">{t('User_Order_PaidOnline')}</span>;
	} else if (appointment?.order?.paymentStatus === OrderPaymentStatus.Rejected) {
		orderStatusElement = <span className="payment-rejected">{t('User_Order_PaymentRejected')}</span>;
	} else {
		orderStatusElement = <span className="payment-at-salon">{t('User_Order_PaymentAtSalon')}</span>;
	}

	const isRecurringEvent = appointment?.orderItem?.[0]?.recurringBookingId ? true : false;

	const appointmentInfo = isShopAppointment ? (
		<div className="appointmentInfoContainer">
			{
				isShopCustomerAppointment ? (
					<>
						<div className="row">
							<div className="col-md-12">
								<p className="appointmentInfoContainer__durationCustomer">
									{momentWithTimezone(timezone, firstStartTime).format('llll')}
									{isRecurringEvent ? renderIcon('recurringEvent', 20, 20) : null}
								</p>
							</div>

							<div className="col-md-12">
								<div className="appointmentInfoContainer__price ">
									<p className="appointmentInfoContainer__upperText">{t('PortalPage_Section_Manage_Common_Price')}</p>
									<p>{formatCurrency(currencyKey, totalPrice || 0)}</p>
								</div>
							</div>

							<div className="col-md-6">
								<div className="appointmentInfoContainer__paymentStatus">
									<p className="appointmentInfoContainer__upperText">{t('User_UserCard_PaymentStatus')}</p>
									{orderStatusElement}
								</div>
							</div>

							<div className="col-md-6">
								<div className="appointmentInfoContainer__orderStatus">
									<p className="appointmentInfoContainer__upperText">{t('User_UserCard_AppointmentStatus')}</p>
									{cancelled && !dontMarkCancelled && (
										<span className="cancelled-indicator">{t('BSPortal_Common_Cancelled')}</span>
									)}
									{booked && !completed && !dontMarkCancelled && (
										<span className="booked-indicator">{t('BSPortal_Common_Booked')}</span>
									)}
									{completed && !dontMarkCancelled && (
										<span className="completed-indicator">{t('BSPortal_Common_Completed')}</span>
									)}
								</div>
							</div>
						</div>
					</>
				) : (
					renderShopServices()
				) // Calendar appointments and single shop order page (renders ungrouped booked order item data)
			}
		</div>
	) : (
		<div className="appointmentInfoContainer">
			<div className="row">
				<div className="col-md-6">
					<div className="appointmentInfoContainer__duration">
						<p className="appointmentInfoContainer__upperText">{t('User_UserCard_DateAndTime')}</p>
						<p>{momentWithTimezone(timezone, firstStartTime).format('llll')}</p>
					</div>
				</div>

				<div className="col-md-6">
					<div className="appointmentInfoContainer__price ">
						<p className="appointmentInfoContainer__upperText">{t('PortalPage_Section_Manage_Common_Price')}</p>
						<p>{formatCurrency(currencyKey, totalPrice)}</p>
					</div>
				</div>

				<div className="col-md-6">
					<div className="appointmentInfoContainer__paymentStatus">
						<p className="appointmentInfoContainer__upperText">{t('User_UserCard_PaymentStatus')}</p>
						{orderStatusElement}
					</div>
				</div>

				<div className="col-md-6">
					<div className="appointmentInfoContainer__orderStatus">
						<p className="appointmentInfoContainer__upperText">{t('User_UserCard_AppointmentStatus')}</p>
						{cancelled && !dontMarkCancelled && (
							<span className="cancelled-indicator">{t('BSPortal_Common_Cancelled')}</span>
						)}
						{booked && !completed && !dontMarkCancelled && (
							<span className="booked-indicator">{t('BSPortal_Common_Booked')}</span>
						)}
						{completed && !dontMarkCancelled && (
							<span className="completed-indicator">{t('BSPortal_Common_Completed')}</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);

	if (onlyAppointmentInfo) {
		return (
			<div className="appointmentCard">
				<div className={`card ${noPadding ? 'noPadding' : ''}`}>{appointmentInfo}</div>
			</div>
		);
	}

	return (
		<div className={`appointmentCard ${customClassName}`}>
			{isShopAppointment ? (
				isShopCustomerAppointment ? (
					<Link
						to={`/shop/${appointment.shopOrder.shopId}/edit/orders/order/${appointment.shopOrder.id}?backUrl=/shop/${appointment.shopOrder.shopId}/customer/${appointment.shopOrder.customerId}`}
					>
						<PortalPageSection>
							<div className="row">
								<div className={'col-lg-12'}>{appointmentInfo}</div>
							</div>
						</PortalPageSection>
					</Link>
				) : (
					<PortalPageSection>
						<div className="row">
							<div className={'col-lg-12'}>{appointmentInfo}</div>
						</div>
					</PortalPageSection>
				)
			) : (
				<Link to={`/userorder/${firstOrderItem.orderId}`}>
					<PortalPageSection>
						<div className="row">
							<div className="col-12">
								<div className={`salonInfoContainer`}>
									<div className="salonInfoWrapper">
										<div className="salonInfoContainer__picture">
											<AvatarSmall imageUrl={firstOrderItem.shopPicture} />
										</div>

										<div className="salonInfoContainer__info">
											<h3 className="salonInfoContainer__info__shopName">{firstOrderItem.shopName}</h3>
											<p className="salonInfoContainer__info__officeLocation">{firstOrderItem.officeName}</p>
										</div>
									</div>

									{isRecurringEvent ? renderIcon('recurringEvent', 20, 20, 'salonInfoContainer__recurringIcon') : null}
								</div>
							</div>

							<div className={'col-12'}>{appointmentInfo}</div>
						</div>
					</PortalPageSection>
				</Link>
			)}
		</div>
	);
});
